import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row, Space, Typography } from "antd";
import React, { useEffect, useRef } from "react";
import { useFieldArray, useFormContext, useFormState } from "react-hook-form";
import { CustomInput } from "../../../../shared/formFields/CustomInput";
import { CustomInputCurrency } from "../../../../shared/formFields/CustomInputCurrency";
import { MonthDayYearInput } from "../../../../shared/formFields/MonthDayYearInput";

const MAX_CLAIMS = 3;
const MAX_DESCRIPTION_LENGTH = 60;

export const emptyAccidentClaim = () => ({
  claim_year: null,
  claim_month: null,
  claim_day: null,
  claim_amount: null,
  claim_description: "",
});

/**
 * Repeating list of accident/claim entries for a single pilot. Rendered only
 * while `has_had_accidents` is true. Each row captures a date (year/month/day),
 * a claim amount and a very brief description; the "+" button adds another row
 * underneath and "-" removes one, matching the phone/membership add-row
 * convention.
 *
 * Submits as pilots[index].accidents_claim_amount:
 *   [{ claim_year, claim_month, claim_day, claim_amount, claim_description }, ...]
 */
export const AccidentClaims = ({ control, index, showErrors }) => {
  const baseName = `pilots[${index}].accidents_claim_amount`;
  const { trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: baseName });
  const { errors } = useFormState({ control, name: baseName });

  // Guarantee at least one row whenever the section becomes visible, no matter
  // how it was mounted (toggled on, or loaded from an existing application).
  const seededRef = useRef(false);
  useEffect(() => {
    if (!seededRef.current && fields.length === 0) {
      seededRef.current = true;
      append(emptyAccidentClaim(), { shouldFocus: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claimErrors = errors?.pilots?.[index]?.accidents_claim_amount;

  return (
    <Form.Item
      label="Accident / claim details"
      required
      style={{ marginBottom: 0 }}
    >
      <Typography.Text
        type="secondary"
        style={{ display: "block", marginBottom: 12, fontSize: 13 }}
      >
        For each accident, claim, license limitation or violation, license
        surrender or revocation, or Transport Canada citation in the last 5
        years, enter the date and the claim amount.
      </Typography.Text>
      {fields.map((item, i) => {
        const rowBase = `${baseName}[${i}]`;
        const isLast = i === fields.length - 1;
        const rowHasError = claimErrors?.[i] != null;

        return (
          <Row key={item.id} gutter={8} align="top" wrap>
            <Col flex="none">
              <MonthDayYearInput
                control={control}
                monthName={`${rowBase}.claim_month`}
                dayName={`${rowBase}.claim_day`}
                yearName={`${rowBase}.claim_year`}
                showErrors={showErrors}
              />
            </Col>
            <Col flex="none">
              <CustomInputCurrency
                control={control}
                name={`${rowBase}.claim_amount`}
                placeholder="Claim amount"
                formItem={{ required: true }}
                shouldHideErrors={true}
                showErrors={showErrors}
                style={{ width: 180 }}
              />
            </Col>
            <Col flex="auto" style={{ minWidth: 220 }}>
              <CustomInput
                control={control}
                name={`${rowBase}.claim_description`}
                placeholder="Optional description (max 60 chars)"
                maxLength={MAX_DESCRIPTION_LENGTH}
                formItem={{ required: false }}
                shouldHideErrors={true}
                showErrors={showErrors}
              />
            </Col>
            <Col flex="none">
              <Space.Compact>
                {fields.length > 1 && (
                  <Button
                    type="link"
                    aria-label="Remove claim"
                    icon={<MinusCircleOutlined />}
                    className="link-button-primary"
                    onClick={() => remove(i)}
                  />
                )}
                {fields.length < MAX_CLAIMS && isLast && (
                  <Button
                    type="link"
                    aria-label="Add claim"
                    icon={<PlusCircleOutlined />}
                    className="link-button-primary"
                    disabled={rowHasError}
                    onClick={async () => {
                      const valid = await trigger(baseName);
                      if (valid) {
                        append(emptyAccidentClaim(), { shouldFocus: false });
                      }
                    }}
                  />
                )}
              </Space.Compact>
            </Col>
          </Row>
        );
      })}
    </Form.Item>
  );
};
